import { authManager } from '../auth-manager.js';
import { escapeHTML } from '../security.js';
import { getProdutosStats } from '../db.js';

export async function renderAdminFeedbackView(container, syncManager) {
  const currentUser = authManager.getUser();

  // Check admin authorization on client side
  if (!currentUser || !currentUser.isAdmin) {
    if (syncManager && syncManager.showToast) {
      syncManager.showToast('Acesso restrito a administradores.', 'error');
    }
    window.location.hash = '#notas';
    return;
  }

  let allFeedbacks = [];
  let pendingNotas = [];
  let stats = { totalUsers: 0, totalNotas: 0, totalValor: 0, totalFamilies: 0, totalFeedbacks: 0, averageRating: 0, totalProdutos: 0, produtosIdentificados: 0, produtosPendentes: 0 };
  let localProdStats = { total: 0, enriquecidos: 0, pendentes: 0 };
  let activeTab = 'metrics';

  try {
    const [fbRes, statsRes, pendingRes, pStats] = await Promise.all([
      authManager.fetchWithAuth('/api/admin/feedbacks'),
      authManager.fetchWithAuth('/api/admin/stats'),
      authManager.fetchWithAuth('/api/admin/pending-notas'),
      getProdutosStats().catch(() => ({ total: 0, enriquecidos: 0, pendentes: 0 }))
    ]);
    localProdStats = pStats || { total: 0, enriquecidos: 0, pendentes: 0 };

    if (fbRes.ok) {
      allFeedbacks = await fbRes.json();
    } else {
      const errData = await fbRes.json().catch(() => ({}));
      container.innerHTML = `
        <div class="glass-panel text-center" style="padding: 2.5rem 1.5rem; max-width: 500px; margin: 2rem auto;">
          <span class="material-symbols-outlined" style="font-size: 48px; color: var(--color-error); margin-bottom: 0.75rem;">lock</span>
          <h2 class="font-bold text-on-surface" style="font-size: 1.25rem;">Acesso Administrativo Negado</h2>
          <p class="text-muted" style="margin: 0.5rem 0 1.5rem 0; font-size: 0.875rem;">
            ${escapeHTML(errData.error || 'Seu usuário não possui permissão de administrador no banco ativo.')}
          </p>
          <a href="#notas" class="btn btn-primary">Voltar para Minhas Notas</a>
        </div>
      `;
      return;
    }

    if (statsRes.ok) {
      stats = await statsRes.json();
    }

    if (pendingRes.ok) {
      pendingNotas = await pendingRes.json();
    }
  } catch (err) {
    console.warn('Failed to load admin data:', err);
    container.innerHTML = `
      <div class="glass-panel text-center" style="padding: 2.5rem 1.5rem; max-width: 500px; margin: 2rem auto;">
        <span class="material-symbols-outlined" style="font-size: 48px; color: var(--color-error); margin-bottom: 0.75rem;">error</span>
        <h2 class="font-bold text-on-surface" style="font-size: 1.25rem;">Erro ao Carregar Painel</h2>
        <p class="text-muted" style="margin: 0.5rem 0 1.5rem 0; font-size: 0.875rem;">
          ${escapeHTML(err.message || 'Falha de comunicação com o servidor')}
        </p>
        <a href="#notas" class="btn btn-secondary">Voltar para Minhas Notas</a>
      </div>
    `;
    return;
  }

  const renderStarBadges = (rating) => {
    let stars = '';
    for (let i = 1; i <= 5; i++) {
      const isFilled = i <= rating;
      stars += `<span class="material-symbols-outlined" style="font-size: 16px; color: ${isFilled ? '#F59E0B' : '#CBD5E1'}; font-variation-settings: 'FILL' ${isFilled ? 1 : 0};">star</span>`;
    }
    return stars;
  };

  const renderFeedbackCards = (feedbacks) => {
    if (!feedbacks || feedbacks.length === 0) {
      return `
        <div class="chart-card text-center text-muted" style="padding: 2.5rem 1rem;">
          <span class="material-symbols-outlined" style="font-size: 36px; color: #94A3B8; margin-bottom: 0.5rem;">rate_review</span>
          <p style="margin: 0; font-size: 0.875rem;">Nenhum feedback recebido até o momento.</p>
        </div>
      `;
    }

    return feedbacks.map(fb => {
      const dateStr = fb.createdAt ? new Date(fb.createdAt).toLocaleDateString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
      }) : '';

      const avatarInitial = escapeHTML((fb.userName || 'U')[0].toUpperCase());
      const avatarContent = fb.userPicture
        ? `<div style="width: 36px; height: 36px; border-radius: 50%; overflow: hidden; background: #E2E8F0; display: flex; align-items: center; justify-content: center; position: relative;">
            <img src="${escapeHTML(fb.userPicture)}" alt="" style="width: 36px; height: 36px; border-radius: 50%; object-fit: cover;" referrerpolicy="no-referrer" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';" />
            <span style="display: none; width: 100%; height: 100%; align-items: center; justify-content: center; font-weight: 700; color: #111827; font-size: 0.85rem;">${avatarInitial}</span>
          </div>`
        : `<div style="width: 36px; height: 36px; border-radius: 50%; background: #E2E8F0; color: #111827; font-weight: 700; display: flex; align-items: center; justify-content: center; font-size: 0.85rem;">
            ${avatarInitial}
          </div>`;

      return `
        <div class="chart-card admin-feedback-card" data-id="${fb.id}" style="padding: 1.25rem; margin-bottom: 1rem;">
          <!-- User header -->
          <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 0.75rem; gap: 0.5rem; flex-wrap: wrap;">
            <div style="display: flex; align-items: center; gap: 0.65rem;">
              ${avatarContent}
              <div>
                <div style="font-weight: 700; font-size: 0.9rem; color: #111827;">${escapeHTML(fb.userName || 'Usuário')}</div>
                <div class="text-muted" style="font-size: 0.75rem;">${escapeHTML(fb.userEmail || '')}</div>
              </div>
            </div>
            <div style="display: flex; align-items: center; gap: 0.5rem;">
              <div style="display: flex; align-items: center;">${renderStarBadges(fb.rating)}</div>
              <span style="font-size: 0.75rem; color: #64748B;">${escapeHTML(dateStr)}</span>
            </div>
          </div>

          <!-- Comment -->
          <p style="font-size: 0.875rem; color: #111827; line-height: 1.5; margin: 0 0 0.85rem 0;">
            ${escapeHTML(fb.comment)}
          </p>

          <!-- Admin Reply Section -->
          <div class="admin-reply-section" style="border-top: 1px solid var(--color-border); padding-top: 0.75rem; margin-top: 0.5rem;">
            ${fb.reply ? `
              <div style="background: #F8FAFC; border-left: 3px solid #10B981; padding: 0.65rem 0.85rem; border-radius: 0.5rem; display: flex; justify-content: space-between; align-items: flex-start;">
                <div>
                  <div style="font-size: 0.78rem; font-weight: 700; color: #047857; margin-bottom: 0.2rem; display: flex; align-items: center; gap: 0.35rem;">
                    <span class="material-symbols-outlined" style="font-size: 15px;">forum</span>
                    Resposta da Equipe
                  </div>
                  <div style="font-size: 0.85rem; color: #111827; line-height: 1.4;">${escapeHTML(fb.reply)}</div>
                </div>
                <button class="btn-edit-reply" data-id="${fb.id}" title="Editar Resposta" style="background: none; border: none; color: #64748B; cursor: pointer; padding: 0.2rem;">
                  <span class="material-symbols-outlined" style="font-size: 16px;">edit</span>
                </button>
              </div>
            ` : `
              <form class="admin-reply-form" data-id="${fb.id}">
                <div style="display: flex; gap: 0.45rem; flex-wrap: wrap;">
                  <input 
                    type="text" 
                    class="admin-reply-input" 
                    placeholder="Escreva uma resposta para o usuário..." 
                    style="flex: 1; min-width: 200px; background: #FFFFFF; border: 1px solid var(--color-border); border-radius: var(--radius-pill); color: #111827; padding: 0.55rem 0.85rem; font-size: 0.82rem; outline: none;"
                    required
                  />
                  <button type="submit" class="btn btn-primary" style="padding: 0.55rem 1rem; font-size: 0.82rem; font-weight: 700; border-radius: var(--radius-pill); white-space: nowrap;">
                    Responder
                  </button>
                </div>
              </form>
            `}
          </div>
        </div>
      `;
    }).join('');
  };

  const formattedValor = (stats.totalValor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  container.innerHTML = `
    <!-- Header & Back Button -->
    <div class="detail-header" style="display: flex; align-items: center; gap: 0.75rem; margin-bottom: 1.25rem;">
      <button class="detail-back" id="btn-back" style="background: #ECEAE3; border: none; color: #111827; cursor: pointer; padding: 0.45rem; border-radius: 50%; display: flex; align-items: center; justify-content: center;">
        <span class="material-symbols-outlined" style="font-size: 20px;">arrow_back</span>
      </button>
      <div>
        <h2 style="margin: 0; font-size: clamp(1.25rem, 4.5vw, 1.6rem); font-weight: 800; color: #111827; line-height: 1.2;">Painel do Administrador</h2>
        <p class="text-muted" style="margin: 0.15rem 0 0 0; font-size: 0.82rem;">Métricas gerais do sistema, feedbacks e permissões.</p>
      </div>
    </div>

    <!-- Horizontal Tabs Selector -->
    <div style="display: flex; gap: 0.35rem; overflow-x: auto; padding-bottom: 2px; margin-bottom: 1.25rem; scrollbar-width: none;" id="admin-tabs-selector">
      <button class="home-scope-pill ${activeTab === 'metrics' ? 'active' : ''}" data-tab="metrics" style="font-size: 0.78rem; padding: 0.4rem 0.85rem; white-space: nowrap;">Métricas do Sistema</button>
      <button class="home-scope-pill ${activeTab === 'feedbacks' ? 'active' : ''}" data-tab="feedbacks" style="font-size: 0.78rem; padding: 0.4rem 0.85rem; white-space: nowrap;">Feedbacks (${allFeedbacks.length})</button>
      <button class="home-scope-pill ${activeTab === 'pending' ? 'active' : ''}" data-tab="pending" style="font-size: 0.78rem; padding: 0.4rem 0.85rem; white-space: nowrap;">Notas Pendentes (${pendingNotas.length})</button>
      <button class="home-scope-pill ${activeTab === 'roles' ? 'active' : ''}" data-tab="roles" style="font-size: 0.78rem; padding: 0.4rem 0.85rem; white-space: nowrap;">Gerenciar Admins</button>
    </div>

    <!-- Content Area Container -->
    <div id="admin-tab-content"></div>
  `;

  const contentContainer = container.querySelector('#admin-tab-content');
  const tabSelector = container.querySelector('#admin-tabs-selector');

  // Tab switching function
  function renderActiveTab() {
    if (activeTab === 'metrics') {
      contentContainer.innerHTML = `
        <div class="chart-card mb-lg" style="padding: 1.25rem; margin-bottom: 1.25rem;">
          <h3 style="font-size: 1.05rem; font-weight: 800; color: #111827; margin: 0 0 1rem 0; display: flex; align-items: center; gap: 0.45rem;">
            <span class="material-symbols-outlined" style="color: #2563EB;">database</span>
            Resumo da Operação
          </h3>
          <div class="kpi-grid" style="display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: 0.75rem;">
            <!-- Card 1: Total de Usuários -->
            <div class="kpi-card" style="padding: 1rem; background: #F8FAFC;">
              <div class="kpi-title" style="font-size: 0.75rem;">Contas Criadas</div>
              <div class="kpi-value font-mono" style="font-size: 1.35rem; color: #2563EB;">${stats.totalUsers || 0}</div>
            </div>

            <!-- Card 2: Total de NFs -->
            <div class="kpi-card" style="padding: 1rem; background: #F8FAFC;">
              <div class="kpi-title" style="font-size: 0.75rem;">NFs Importadas</div>
              <div class="kpi-value font-mono" style="font-size: 1.35rem; color: #10B981;">${stats.totalNotas || 0}</div>
            </div>

            <!-- Card 3: Volume de Gastos -->
            <div class="kpi-card" style="padding: 1rem; background: #F8FAFC;">
              <div class="kpi-title" style="font-size: 0.75rem;">Volume Mapeado</div>
              <div class="kpi-value font-mono" style="font-size: 1.2rem; color: #111827;">R$ ${formattedValor}</div>
            </div>

            <!-- Card 4: Grupos Familiares -->
            <div class="kpi-card" style="padding: 1rem; background: #F8FAFC;">
              <div class="kpi-title" style="font-size: 0.75rem;">Grupos Familiares</div>
              <div class="kpi-value font-mono" style="font-size: 1.35rem; color: #8B5CF6;">${stats.totalFamilies || 0}</div>
            </div>

            <!-- Card 5: Feedbacks & Rating -->
            <div class="kpi-card" style="padding: 1rem; background: #F8FAFC;">
              <div class="kpi-title" style="font-size: 0.75rem;">Média Avaliações</div>
              <div class="kpi-value font-mono" style="font-size: 1.35rem; color: #D97706;">
                ${(stats.averageRating || 0).toFixed(1)} <span style="font-size: 0.8rem; color: #64748B;">/ 5.0</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Card Consolidado: Catálogo de Produtos -->
        <div class="chart-card mb-lg" style="padding: 1.25rem; margin-bottom: 1.25rem;">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem; flex-wrap: wrap; gap: 0.5rem;">
            <h3 style="font-size: 1.05rem; font-weight: 800; color: #111827; margin: 0; display: flex; align-items: center; gap: 0.45rem;">
              <span class="material-symbols-outlined" style="color: #2563EB;">inventory_2</span>
              Catálogo de Produtos (Base Colaborativa)
            </h3>
            <a href="#produtos" class="btn btn-secondary" style="font-size: 0.76rem; font-weight: 800; padding: 0.35rem 0.8rem; border-radius: var(--radius-pill); text-decoration: none; display: inline-flex; align-items: center; gap: 0.25rem;">
              <span>Ver Catálogo</span>
              <span class="material-symbols-outlined" style="font-size: 15px;">arrow_forward</span>
            </a>
          </div>

          <div class="kpi-grid" style="display: grid; grid-template-columns: repeat(auto-fit, minmax(130px, 1fr)); gap: 0.75rem;">
            <div class="kpi-card" style="padding: 0.9rem 1rem; background: #F8FAFC; border-radius: 0.85rem;">
              <div class="kpi-title" style="font-size: 0.72rem; font-weight: 700; color: #64748B; text-transform: uppercase;">Total de Itens</div>
              <div class="kpi-value font-mono" style="font-size: 1.35rem; font-weight: 900; color: #111827; margin-top: 0.2rem;">
                ${stats.totalProdutos || localProdStats.total || 0}
              </div>
            </div>
            <div class="kpi-card" style="padding: 0.9rem 1rem; background: #F8FAFC; border-radius: 0.85rem;">
              <div class="kpi-title" style="font-size: 0.72rem; font-weight: 700; color: #10B981; text-transform: uppercase;">Identificados ✨</div>
              <div class="kpi-value font-mono" style="font-size: 1.35rem; font-weight: 900; color: #10B981; margin-top: 0.2rem;">
                ${stats.produtosIdentificados || localProdStats.enriquecidos || 0}
              </div>
            </div>
            <div class="kpi-card" style="padding: 0.9rem 1rem; background: #F8FAFC; border-radius: 0.85rem;">
              <div class="kpi-title" style="font-size: 0.72rem; font-weight: 700; color: #D97706; text-transform: uppercase;">Pendentes ⏳</div>
              <div class="kpi-value font-mono" style="font-size: 1.35rem; font-weight: 900; color: #D97706; margin-top: 0.2rem;">
                ${stats.produtosPendentes || localProdStats.pendentes || 0}
              </div>
            </div>
          </div>
        </div>

        <!-- Usuários que Começaram a Utilizar o App -->
        <div class="chart-card mb-lg" style="padding: 1.25rem;">
          <h3 style="font-size: 1.05rem; font-weight: 800; color: #111827; margin: 0 0 1rem 0; display: flex; align-items: center; gap: 0.45rem;">
            <span class="material-symbols-outlined" style="color: #2563EB;">person_check</span>
            Usuários Recentes (${(stats.recentUsers || []).length})
          </h3>

          ${(stats.recentUsers && stats.recentUsers.length > 0) ? `
            <div style="display: flex; flex-direction: column; gap: 0.75rem;">
              ${stats.recentUsers.map(u => {
                const createdDate = u.createdAt ? new Date(u.createdAt).toLocaleDateString('pt-BR', {
                  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                }) : 'Recente';

                const avatarInitial = escapeHTML((u.name || 'U')[0].toUpperCase());
                const avatarContent = u.picture
                  ? `<div style="width: 40px; height: 40px; border-radius: 50%; overflow: hidden; background: #E2E8F0; display: flex; align-items: center; justify-content: center; position: relative;">
                      <img src="${escapeHTML(u.picture)}" alt="" style="width: 40px; height: 40px; border-radius: 50%; object-fit: cover;" referrerpolicy="no-referrer" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';" />
                      <span style="display: none; width: 100%; height: 100%; align-items: center; justify-content: center; font-weight: 800; color: #111827; font-size: 0.9rem;">${avatarInitial}</span>
                    </div>`
                  : `<div style="width: 40px; height: 40px; border-radius: 50%; background: #E2E8F0; color: #111827; font-weight: 800; display: flex; align-items: center; justify-content: center; font-size: 0.9rem;">
                      ${avatarInitial}
                    </div>`;

                const gastoStr = (u.totalGasto || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

                return `
                  <div style="background: #F8FAFC; border: 1px solid var(--color-border); border-radius: 0.75rem; padding: 0.85rem 1rem; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 0.75rem;">
                    <div style="display: flex; align-items: center; gap: 0.75rem;">
                      ${avatarContent}
                      <div>
                        <div style="display: flex; align-items: center; gap: 0.35rem; flex-wrap: wrap;">
                          <span style="font-weight: 700; font-size: 0.9rem; color: #111827;">${escapeHTML(u.name)}</span>
                          ${u.isAdmin ? `<span style="background: #FEF3C7; color: #B45309; font-size: 0.68rem; font-weight: 700; padding: 0.1rem 0.45rem; border-radius: var(--radius-pill);">ADMIN</span>` : ''}
                          ${u.familyName ? `<span style="background: #EDE9FE; color: #6D28D9; font-size: 0.68rem; font-weight: 600; padding: 0.1rem 0.45rem; border-radius: var(--radius-pill);">${escapeHTML(u.familyName)}</span>` : ''}
                        </div>
                        <div class="text-muted" style="font-size: 0.75rem;">${escapeHTML(u.email)}</div>
                        <div style="font-size: 0.72rem; color: #64748B; margin-top: 0.15rem;">
                          Entrou em: <strong>${escapeHTML(createdDate)}</strong>
                        </div>
                      </div>
                    </div>

                    <div style="display: flex; gap: 1.25rem; align-items: center; flex-wrap: wrap;">
                      <div style="text-align: right;">
                        <div class="text-muted" style="font-size: 0.72rem;">NFs Importadas</div>
                        <div class="font-mono font-bold" style="font-size: 1.05rem; color: #111827;">${u.totalNotas || 0}</div>
                      </div>
                      <div style="text-align: right;">
                        <div class="text-muted" style="font-size: 0.72rem;">Volume Mapeado</div>
                        <div class="font-mono font-bold" style="font-size: 1.05rem; color: #10B981;">R$ ${gastoStr}</div>
                      </div>
                    </div>
                  </div>
                `;
              }).join('')}
            </div>
          ` : `
            <div class="chart-card text-center text-muted" style="padding: 1.5rem; font-size: 0.85rem;">
              Nenhum usuário cadastrado encontrado.
            </div>
          `}
        </div>
      `;
    } else if (activeTab === 'feedbacks') {
      contentContainer.innerHTML = `
        <div class="chart-card mb-lg" style="padding: 1.25rem;">
          <h3 style="font-size: 1.05rem; font-weight: 800; color: #111827; margin: 0 0 1rem 0; display: flex; align-items: center; gap: 0.4rem;">
            <span class="material-symbols-outlined" style="color: #D97706;">rate_review</span>
            Todos os Feedbacks dos Usuários (${allFeedbacks.length})
          </h3>
          <div id="admin-feedbacks-container">
            ${renderFeedbackCards(allFeedbacks)}
          </div>
        </div>
      `;
    } else if (activeTab === 'pending') {
      contentContainer.innerHTML = `
        <div class="chart-card mb-lg" style="padding: 1.25rem;">
          <h3 style="font-size: 1.05rem; font-weight: 800; color: #111827; margin: 0 0 0.35rem 0; display: flex; align-items: center; gap: 0.4rem;">
            <span class="material-symbols-outlined" style="color: #D97706; font-size: 20px;">hourglass_top</span>
            Notas Pendentes de Compatibilidade (${pendingNotas.length})
          </h3>
          <p class="text-muted" style="font-size: 0.82rem; margin: 0 0 1rem 0;">
            Cupons fiscais escaneados com formato ou estado ainda não mapeados. Utilize as URLs abaixo para depuração e criação de novos parsers.
          </p>
          ${pendingNotas.length === 0 ? `
            <div class="chart-card text-center text-muted" style="padding: 2rem 1rem;">
              <span class="material-symbols-outlined" style="font-size: 32px; color: #10B981; margin-bottom: 0.4rem;">check_circle</span>
              <p style="margin: 0; font-size: 0.85rem;">Nenhuma nota pendente de compatibilidade no momento.</p>
            </div>
          ` : `
            <div style="display: flex; flex-direction: column; gap: 0.65rem;">
              ${pendingNotas.map(pn => `
                <div style="background: #FEF3C720; border: 1px solid #FDE68A; border-radius: 0.75rem; padding: 0.85rem;">
                  <div style="display: flex; justify-content: space-between; align-items: center; font-size: 0.82rem; margin-bottom: 0.35rem; flex-wrap: wrap; gap: 0.3rem;">
                    <span style="font-weight: 700; color: #B45309;">${escapeHTML(pn.emitente?.razaoSocial || 'NFC-e Outros Estados')}</span>
                    <span style="font-size: 0.75rem; color: #64748B;">${escapeHTML(pn.createdAt || '')}</span>
                  </div>
                  <div style="font-family: var(--font-mono); font-size: 0.78rem; color: #111827; word-break: break-all; margin-bottom: 0.35rem;">
                    URL: <a href="${escapeHTML(pn.url)}" target="_blank" style="color: #2563EB; text-decoration: underline;">${escapeHTML(pn.url)}</a>
                  </div>
                  ${pn.chaveAcesso ? `<div style="font-size: 0.75rem; color: #64748B;">Chave: ${escapeHTML(pn.chaveAcesso)}</div>` : ''}
                </div>
              `).join('')}
            </div>
          `}
        </div>
      `;
    } else if (activeTab === 'roles') {
      contentContainer.innerHTML = `
        <div class="chart-card mb-lg" style="padding: 1.25rem;">
          <h3 style="font-size: 1.05rem; font-weight: 800; color: #111827; margin: 0 0 0.35rem 0; display: flex; align-items: center; gap: 0.4rem;">
            <span class="material-symbols-outlined" style="color: #2563EB; font-size: 20px;">admin_panel_settings</span>
            Gerenciar Privilégios de Administradores
          </h3>
          <p class="text-muted" style="font-size: 0.82rem; margin: 0 0 1rem 0;">
            Conceda acesso irrestrito ao painel administrativo e ferramentas de suporte para outros membros da equipe por e-mail.
          </p>
          <form id="admin-role-form" style="display: flex; gap: 0.5rem; flex-wrap: wrap; align-items: center;">
            <input 
              type="email" 
              id="admin-email-input" 
              placeholder="Digite o e-mail do usuário (ex: membro@gmail.com)..." 
              style="flex: 1; min-width: 240px; background: #FFFFFF; border: 1px solid var(--color-border); border-radius: 0.65rem; color: #111827; padding: 0.65rem 0.85rem; font-size: 0.85rem; outline: none;"
              required
            />
            <button type="submit" class="btn btn-primary" style="padding: 0.65rem 1.25rem; font-weight: 700; font-size: 0.85rem; border-radius: var(--radius-pill);">
              Conceder Admin
            </button>
          </form>
        </div>
      `;

      // Bind Role Form
      const roleForm = contentContainer.querySelector('#admin-role-form');
      roleForm?.addEventListener('submit', async (e) => {
        e.preventDefault();
        const emailInput = contentContainer.querySelector('#admin-email-input');
        const email = emailInput?.value.trim();
        if (!email) return;

        try {
          const res = await authManager.fetchWithAuth('/api/admin/users/role', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: email, isAdmin: true })
          });

          if (!res.ok) {
            const errData = await res.json();
            throw new Error(errData.error || 'Erro ao conceder privilégios');
          }

          emailInput.value = '';
          if (syncManager && syncManager.showToast) {
            syncManager.showToast(`Privilégios de admin concedidos a ${email}`, 'success');
          }
        } catch (err) {
          console.error('Error promoting admin:', err);
          if (syncManager && syncManager.showToast) {
            syncManager.showToast(err.message || 'Falha ao promover usuário.', 'error');
          }
        }
      });
    }
  }

  // Initial render
  renderActiveTab();

  // Tab click listeners
  tabSelector.querySelectorAll('.home-scope-pill').forEach(btn => {
    btn.addEventListener('click', () => {
      tabSelector.querySelectorAll('.home-scope-pill').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      activeTab = btn.getAttribute('data-tab');
      renderActiveTab();
    });
  });

  // Bind Back Button
  container.querySelector('#btn-back')?.addEventListener('click', () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      window.location.hash = '#notas';
    }
  });

  // Delegate Reply Form Submissions & Edit Reply
  container.addEventListener('submit', async (e) => {
    const form = e.target.closest('.admin-reply-form');
    if (!form) return;
    e.preventDefault();

    const feedbackID = form.dataset.id;
    const inputEl = form.querySelector('.admin-reply-input');
    const replyText = inputEl?.value.trim();
    if (!feedbackID || !replyText) return;

    try {
      const res = await authManager.fetchWithAuth(`/api/admin/feedbacks/${feedbackID}/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reply: replyText })
      });

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || 'Erro ao salvar resposta');
      }

      const updatedFb = await res.json();
      const index = allFeedbacks.findIndex(f => f.id === feedbackID);
      if (index !== -1) {
        allFeedbacks[index] = updatedFb;
      }

      const listContainer = container.querySelector('#admin-feedbacks-container');
      if (listContainer) {
        listContainer.innerHTML = renderFeedbackCards(allFeedbacks);
      }

      if (syncManager && syncManager.showToast) {
        syncManager.showToast('Resposta registrada com sucesso!', 'success');
      }
    } catch (err) {
      console.error('Error replying feedback:', err);
      if (syncManager && syncManager.showToast) {
        syncManager.showToast(err.message || 'Falha ao responder feedback.', 'error');
      }
    }
  });

  // Delegate Edit Reply Click
  container.addEventListener('click', (e) => {
    const editBtn = e.target.closest('.btn-edit-reply');
    if (editBtn) {
      const feedbackID = editBtn.dataset.id;
      const card = container.querySelector(`.admin-feedback-card[data-id="${feedbackID}"]`);
      if (card) {
        const fb = allFeedbacks.find(f => f.id === feedbackID);
        const replySection = card.querySelector('.admin-reply-section');
        if (replySection && fb) {
          replySection.innerHTML = `
            <form class="admin-reply-form" data-id="${fb.id}">
              <div style="display: flex; gap: 0.5rem; flex-wrap: wrap;">
                <input 
                  type="text" 
                  class="admin-reply-input" 
                  value="${escapeHTML(fb.reply)}" 
                  style="flex: 1; min-width: 200px; background: rgba(0,0,0,0.3); border: 1px solid rgba(255,255,255,0.12); border-radius: 0.6rem; color: var(--color-on-surface); padding: 0.6rem 0.85rem; font-size: 0.85rem;"
                  required
                />
                <button type="submit" class="btn btn-primary btn-sm" style="padding: 0.6rem 1rem; font-size: 0.85rem; font-weight: 700;">
                  Atualizar Resposta
                </button>
              </div>
            </form>
          `;
        }
      }
    }
  });
}
